"use client";
import { RetailCart } from "@/models/cart/retailCart";
import { ServiceCart } from "@/models/cart/serviceCart";
import { lightenColor } from "@/utils/lightenColor";
import { removeFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import useMediaQuery from "@/utils/useMediaQuery";
import { isEmpty } from "lodash";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { BorderedButton } from "../button/borderedButton";
import { ConfirmButton } from "../button/confirmButton";
import { RegularModal } from "../modals/modals";
import { useId } from "react";

export interface ClearCartAction {
  text: string;
  onClick: () => void;
  type: "primary" | "secondary";
}
interface ClearCartModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  merchantColor: string;
  addToCart?: () => void;
  actions?: ClearCartAction[];
  title?: string;
  message?: string;
}

export const ClearCartModal = ({
  isOpen,
  onRequestClose,
  merchantColor,
  addToCart,
  actions,
  title,
  message,
}: ClearCartModalProps) => {
  const t = useTranslations("ClearCartModal");
  const pathname = usePathname();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const titleId = useId();
  const subtitleId = useId();

  const isServicePage: boolean = pathname?.includes("services") || false;

  const handleCartClear = () => {
    if (isServicePage) {
      RetailCart.clear();
      removeFromLocalStorage(LocalStorageKey.GIFT_CARD);
    } else {
      ServiceCart.clear();
    }
    if (typeof addToCart === "function") {
      addToCart();
    }
    onRequestClose();
  };

  const defaultActions: ClearCartAction[] = [
    {
      text: t("clearCart"),
      onClick: handleCartClear,
      type: "secondary",
    },
    {
      text: t("goBack"),
      onClick: onRequestClose,
      type: "primary",
    },
  ];

  const modalActions = isEmpty(actions) ? defaultActions : actions;

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
      contentClassName="bg-white sm:rounded-lg shadow-lg w-[100%] h-[100%] sm:h-auto sm:w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto overflow-hidden"
      aria={{ labelledby: titleId, describedby: subtitleId }}
    >
      <h2
        id={titleId}
        aria-describedby={subtitleId}
        className="text-2xl font-semibold sm:font-medium text-[#373737] px-4 pt-4"
      >
        {title || t(isServicePage ? "retailItemsTitle" : "servicesTitle")}
      </h2>
      <div className="pt-4 px-4">
        <p id={subtitleId} className="text-[15px] text-md text-[#000000]">
          {message ||
            t(isServicePage ? "retailItemsMessage" : "servicesMessage")}
        </p>
      </div>
      <div className="sm:static fixed bottom-0 left-0 w-full z-50 sm:z-auto">
        <div className="flex flex-col-reverse sm:flex-row justify-end sm:gap-4 items-center sm:p-4 max-w-md md:max-w-lg lg:max-w-xl mx-auto bg-white">
          {modalActions?.map((action, index) => {
            const { type, text, onClick } = action;
            return type === "primary" ? (
              <ConfirmButton
                key={`${text}-${index}`}
                style={{
                  backgroundColor: isMobileScreen
                    ? lightenColor(merchantColor, 90)
                    : merchantColor,
                  color: isMobileScreen ? "black" : "white",
                }}
                onClick={onClick}
                className="w-full sm:w-auto h-16 sm:h-auto text-black sm:text-white disabled:text-black py-2 rounded-none sm:rounded disabled:bg-[#E5E5E5]"
              >
                {text}
              </ConfirmButton>
            ) : (
              <div key={`${text}-${index}`} className="w-full sm:w-auto">
                <BorderedButton
                  style={{
                    color: isMobileScreen ? "white" : merchantColor,
                    border: isMobileScreen ? "none" : `solid ${merchantColor}`,
                    backgroundColor: isMobileScreen ? merchantColor : "white",
                  }}
                  className="w-full flex justify-center h-16 sm:h-auto rounded-none sm:rounded"
                  onClick={onClick}
                >
                  {text}
                </BorderedButton>
              </div>
            );
          })}
        </div>
      </div>
    </RegularModal>
  );
};
